/**
 * 学习乐园 · 大洲大洋归位（game-dazhou-dayang.js）
 * ============================================================
 * 「版图归位」第三作：独立世界底图（全局 STWORLD_MAP，由
 * scripts/make-study-world-map.mjs 从 Natural Earth 110m 生成）。
 * 七大洲 + 四大洋共 11 个要素：大洲是合并轮廓（俄罗斯按乌拉尔山口径
 * 拆归欧亚，见底图脚本），大洋不存面——命中按通行分界口径的规则现算：
 *   北冰洋/其余   ：65°N 一线（白令海峡、格陵兰—苏格兰一线的简化）
 *   大西洋/印度洋 ：20°E（厄加勒斯角）
 *   印度洋/太平洋 ：147°E（塔斯马尼亚）→ 142°E（托雷斯海峡）→
 *                   125°E（马鲁古）→ 100°E（马来半岛）分段
 *   太平洋/大西洋 ：美洲西岸一线 → 67°W（合恩角）以南
 *   分界两侧 teaching 点放错时讲给你听；规则常量与底图脚本互检（改两处同改）。
 *
 * 交互（点选—点放两步，与省份拼图 / 山河归位同款，不依赖拖拽）：
 *   托盘点名字 → 点地图上对应的大洲轮廓或海域。
 *   陆地命中 = 点在各洲闭环点列内（射线法，evenodd——里海这类内水面成洞）；
 *   海面命中 = oceanAt 规则。
 *
 * 反馈底线（同系列）：
 *   放对了 → 数据里手写的一句讲解（最大的洲、最深处、分界在哪）；
 *   放错了 → 告诉你落点是谁：点到别的大洲讲那洲 + 目标在它哪个方向；
 *            点到别的大洋讲那洋 + 目标方位（方位由锚点实算，不是编好的台词）；
 *            点到里海（世界最大的湖）如实说不参与归位。
 *
 * 示意口径（页面文案同步说明，见 doc/dazhou-dayang.html）：
 *   教学示意轮廓，非精确边界；大洋分界按通行口径简化。
 *
 * 简化红线（同前两作）：不做中断续局，刷新即重开；成绩只存本机
 * localStorage，不上传。
 *
 * 冒烟钩子：window.STWorld 暴露纯引擎函数，供确定性用例；
 * 交互路径另有 scripts/smoke-dazhou-dom.mjs（vm 级 DOM stub 整链路）。
 */
(function (global) {
  'use strict';

  var MAP = global.STWORLD_MAP;
  var RECORD_KEY = 'study-world-record';
  var HINT_MS = 2200;
  var VB = (MAP.viewBox.split(' ')).map(Number); // [0, 0, W, H]

  /* ============ 纯引擎（无 DOM） ============ */

  /** 射线法：点是否在闭环集合内（evenodd 语义——外环与洞自然成立） */
  function pointInRings(x, y, loops) {
    var inside = false;
    for (var k = 0; k < loops.length; k++) {
      var ring = loops[k];
      for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        var xi = ring[i][0], yi = ring[i][1];
        var xj = ring[j][0], yj = ring[j][1];
        if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) inside = !inside;
      }
    }
    return inside;
  }

  /** viewBox 坐标 → 经纬度（等距圆柱反算，proj 由底图脚本生成） */
  function lonLatAt(x, y) {
    var p = MAP.proj;
    return [(x - p.m) / p.k - 180, p.latTop - (y - p.m) / p.k];
  }

  /**
   * 海面属哪个大洋（教学通行口径；与 make-study-world-map.mjs 的 oceanAt 互为镜像，
   * 改要两处同改）。只对「水点」调用——陆地点在调用方先判。
   */
  function oceanAt(lon, lat) {
    if (lat >= 65) return 'arctic';                       // 北冰洋：65°N 一线以北
    var ip = lat >= 8 ? 100 : (lat >= -8 ? 125 : (lat >= -40 ? 142 : 147)); // 印度洋东界
    var wp = lat >= 8 ? -100 : (lat >= -18 ? -80 : (lat >= -55 ? -72 : -67)); // 大西洋西界（美洲一线）
    if (lon >= 20 && lon < ip && lat < 30) return 'indian'; // 印度洋：20°E 以东、北界 30°N（红海/波斯湾在内）
    if (lon >= ip || lon < wp) return 'pacific';          // 太平洋：印度洋以东 / 美洲一线以西
    return 'atlantic';                                    // 其余（含地中海、黑海——通行口径属大西洋水域）
  }

  /** 里海盒：世界最大的湖，内流水域，不参与大洋归位（教学上单独讲） */
  function isCaspian(lon, lat) {
    return lat >= 36 && lat <= 48 && lon >= 45 && lon <= 55;
  }

  /** 落点大洲：逐洲射线法（evenodd），返回 continent 或 null（水） */
  function continentAt(x, y) {
    for (var i = 0; i < MAP.continents.length; i++) {
      var c = MAP.continents[i];
      if (pointInRings(x, y, c.points)) return c;
    }
    return null;
  }

  /** 8 方位词：a 在 b 的哪个方向（统一锚点 ax/ay；y 向下 = 向南） */
  function bearingWord(a, b) {
    var dx = a.ax - b.ax, dy = a.ay - b.ay;
    if (Math.hypot(dx, dy) < 34) return '就在旁边';
    var deg = Math.atan2(dy, dx) / Math.PI * 180; // 0° 正东、90° 正南
    var words = ['正东', '东南', '正南', '西南', '正西', '西北', '正北', '东北'];
    return words[Math.round(((deg + 360) % 360) / 45) % 8];
  }

  /** 提示用的模糊方位（不剧透形状）：半球 + 图上三分位 */
  function describeZone(f) {
    var z = f.ay < MAP.equator.y ? '北半球' : '南半球';
    if (f.ax < VB[2] / 3) z += '、图上偏左';
    else if (f.ax > VB[2] * 2 / 3) z += '、图上偏右';
    return z;
  }

  function shuffledIds(n) {
    var ids = [];
    for (var i = 0; i < n; i++) ids.push(i);
    for (var j = n - 1; j > 0; j--) {
      var k = Math.floor(Math.random() * (j + 1));
      var t = ids[j]; ids[j] = ids[k]; ids[k] = t;
    }
    return ids;
  }

  /* ============ 渲染与交互 ============ */

  var NS = 'http://www.w3.org/2000/svg';
  var state = {
    selected: null, placed: {}, placedCount: 0,
    miss: 0, hints: 0, elapsed: 0, timer: null, done: false,
  };
  var el = {};
  var svg, pieces, labels, nodes;

  function svgEl(tag, attrs) {
    var n = document.createElementNS(NS, tag);
    for (var k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  }

  function fmtTime(ms) {
    var s = Math.floor(ms / 1000);
    return Math.floor(s / 60) + ' 分 ' + (s % 60) + ' 秒';
  }

  function say(msg, tone) {
    var TONE_CLS = { ok: '', bad: 'is-warn', done: 'is-over' };
    el.status.textContent = msg;
    el.status.className = 'st-status' + (tone && TONE_CLS[tone] ? ' ' + TONE_CLS[tone] : '');
  }

  function updateHud() {
    el.progress.textContent = '已归位 ' + state.placedCount + ' / ' + pieces.length;
    el.time.textContent = '用时 ' + fmtTime(state.elapsed);
  }

  function loadRecord() {
    try { return JSON.parse(localStorage.getItem(RECORD_KEY)) || {}; }
    catch (e) { return {}; }
  }

  function renderRecord() {
    var rec = loadRecord();
    if (!rec.bestMs) {
      el.record.innerHTML = '<p class="st-map-record-empty">还没有完整归位的记录——放完第一局就有了。</p>';
      return;
    }
    el.record.innerHTML =
      '<p>最快完整归位：<strong>' + fmtTime(rec.bestMs) + '</strong>（错放 ' + rec.bestMiss + ' 次）</p>' +
      '<p class="st-map-record-sub">完整归位 ' + (rec.plays || 1) + ' 局 · 成绩只存这台设备</p>';
  }

  function saveRecord(rec) {
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 存不进就算了 */ }
  }

  /* ---- SVG 搭建：赤道参照线 → 大洲轮廓 → 大洋标记 → 标注层 ---- */

  function buildSvg() {
    svg = svgEl('svg', {
      viewBox: MAP.viewBox,
      class: 'st-map-svg',
      role: 'application',
      'aria-label': '世界地图：把托盘里的七大洲四大洋放回对应位置',
    });
    nodes = {};
    labels = {};

    // 1) 赤道浅虚线（方位参照，标注在线右端）
    svg.appendChild(svgEl('line', {
      x1: 0, y1: MAP.equator.y, x2: VB[2], y2: MAP.equator.y,
      class: 'st-map-equator',
    }));
    var eq = svgEl('text', { x: VB[2] - 6, y: MAP.equator.y - 5, class: 'st-map-note', 'text-anchor': 'end' });
    eq.textContent = MAP.equator.label;
    svg.appendChild(eq);

    // 2) 大洲轮廓：灰虚线槽（归位后亮品牌色），形状来自底图数据
    pieces.forEach(function (f) {
      if (f.kind !== 'continent') return;
      var n = svgEl('path', { d: f.ref.path, 'fill-rule': 'evenodd', class: 'st-map-slot' });
      n.dataset.id = f.id;
      nodes[f.id] = n;
      svg.appendChild(n);
    });

    // 3) 大洋标记：海面上的圆点（归位后品牌实点）——海面没有轮廓可描，
    //    标记点是「这里可以点」的视觉锚，命中判定仍按 oceanAt 规则算整片海。
    //    r=8（2026-10-01 用户反馈点太小难点中；手机上整图缩到 ~1/3，r=5 只剩 2px）
    pieces.forEach(function (f) {
      if (f.kind !== 'ocean') return;
      var n = svgEl('circle', { cx: f.ref.ax, cy: f.ref.ay, r: 8, class: 'st-map-omark' });
      n.dataset.id = f.id;
      nodes[f.id] = n;
      svg.appendChild(n);
    });

    // 4) 标注层：归位后逐个亮名字（大洲标在锚点上方，大洋标在标记下方，让开 r=8 的点）
    pieces.forEach(function (f) {
      var x = f.ref.cx != null ? f.ref.cx : f.ref.ax;
      var y = f.kind === 'continent' ? f.ref.cy - 8 : f.ref.ay + 20;
      var t = svgEl('text', { x: x, y: y, class: 'st-map-label', 'text-anchor': 'middle', visibility: 'hidden' });
      t.textContent = f.name;
      labels[f.id] = t;
      svg.appendChild(t);
    });

    el.map.innerHTML = '';
    el.map.appendChild(svg);
    svg.addEventListener('click', onPick);
  }

  function selectPiece(f) {
    state.selected = f;
    var btns = el.tray.querySelectorAll('[data-st-piece]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].classList.toggle('is-active', btns[i].dataset.stPiece === f.id);
    }
    say('已选中「' + f.name + '」——点' + (f.kind === 'continent' ? '它的轮廓' : '它的海域') + '；放错了我会告诉你那里是谁。');
  }

  function clearSelection() {
    state.selected = null;
    var btns = el.tray.querySelectorAll('[data-st-piece]');
    for (var i = 0; i < btns.length; i++) btns[i].classList.remove('is-active');
  }

  function placeCorrect(f) {
    state.placed[f.id] = true;
    state.placedCount++;
    nodes[f.id].classList.add('is-placed');
    var btn = el.tray.querySelector('[data-st-piece="' + f.id + '"]');
    if (btn) {
      btn.classList.add('is-done');
      btn.disabled = true;
      btn.removeAttribute('data-st-piece');
    }
    labels[f.id].setAttribute('visibility', 'visible');
    clearSelection();
    updateHud();
    if (state.placedCount === pieces.length) {
      finish();
    } else {
      say('对！' + f.tip + '（' + state.placedCount + '/' + pieces.length + '）', 'ok');
    }
  }

  function finish() {
    state.done = true;
    stopTimer();
    var rec = loadRecord();
    var first = !rec.bestMs;
    var isNew = first || state.elapsed < rec.bestMs;
    var prevBest = rec.bestMs;
    rec.bestMs = first ? state.elapsed : Math.min(rec.bestMs, state.elapsed);
    if (isNew) rec.bestMiss = state.miss;
    rec.plays = (rec.plays || 0) + 1;
    saveRecord(rec);
    renderRecord();
    var extra = first
      ? '这是你的第一个完整归位记录！'
      : (isNew ? '新纪录！此前最快是 ' + fmtTime(prevBest) + '。' : '此前最快是 ' + fmtTime(rec.bestMs) + '。');
    say('七大洲四大洋全部归位！用时 ' + fmtTime(state.elapsed) + '，错放 ' + state.miss +
      ' 次，提示 ' + state.hints + ' 次。' + extra, 'done');
  }

  /* ---- 落点判定与讲解 ---- */

  function onPick(ev) {
    if (state.done) return;
    var rect = svg.getBoundingClientRect();
    var x = (ev.clientX - rect.left) / rect.width * VB[2];
    var y = (ev.clientY - rect.top) / rect.height * VB[3];

    if (!state.selected) {
      // 未选中：不剧透——报一下落点是谁，请先去托盘挑名字
      var cont0 = continentAt(x, y);
      if (cont0) {
        say('这里是' + cont0.name + '——先在托盘里挑一个名字，再点地图放它。');
      } else {
        var ll0 = lonLatAt(x, y);
        if (isCaspian(ll0[0], ll0[1])) {
          say('这里是里海——世界最大的湖，不参与大洋归位。先在托盘里挑一个名字。');
        } else {
          var o0 = OCEAN_BY_ID[oceanAt(ll0[0], ll0[1])];
          say('这里是' + o0.name + '的海面——先在托盘里挑一个名字，再点地图放它。');
        }
      }
      return;
    }

    var sel = state.selected;
    var cont = continentAt(x, y);
    if (cont) {
      if (sel.kind === 'continent' && sel.id === cont.id) {
        placeCorrect(sel);
        return;
      }
      state.miss++;
      if (state.placed[cont.id]) {
        say(cont.name + '已经归位了——' + sel.name + '在它的' + bearingWord(sel, cont) + '。', 'bad');
      } else {
        say('这里是' + cont.name + '——' + sel.name + '在它的' + bearingWord(sel, cont) + '。', 'bad');
      }
      clearSelection();
      return;
    }

    var ll = lonLatAt(x, y);
    if (isCaspian(ll[0], ll[1])) {
      state.miss++;
      say('这里是里海——世界最大的湖，不参与大洋归位；' + sel.name + '在' + describeZone(sel) + '。', 'bad');
      clearSelection();
      return;
    }
    var ocean = OCEAN_BY_ID[oceanAt(ll[0], ll[1])];
    if (sel.kind === 'ocean' && sel.id === ocean.id) {
      placeCorrect(sel);
      return;
    }
    state.miss++;
    say('这里是' + ocean.name + '——' + sel.name + '在它的' + bearingWord(sel, ocean) + '。', 'bad');
    clearSelection();
  }

  /* ---- 托盘 / 计时 / 按钮 ---- */

  function buildTray() {
    el.tray.innerHTML = '';
    // 分组：大洲在前、大洋在后，组内乱序（同款洗牌）
    ['continent', 'ocean'].forEach(function (kind) {
      var head = document.createElement('span');
      head.className = 'st-map-tray-head';
      head.textContent = kind === 'continent' ? '大洲' : '大洋';
      el.tray.appendChild(head);
      var list = pieces.filter(function (f) { return f.kind === kind; });
      shuffledIds(list.length).forEach(function (idx) {
        var f = list[idx];
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'st-map-piece';
        b.dataset.stPiece = f.id;
        b.textContent = f.name;
        b.addEventListener('click', function () {
          if (state.done) return;
          if (state.selected && state.selected.id === f.id) {
            clearSelection();
            say('已取消选中。');
            return;
          }
          selectPiece(f);
        });
        el.tray.appendChild(b);
      });
    });
  }

  function stopTimer() {
    if (state.timer) { clearInterval(state.timer); state.timer = null; }
  }

  /** 开新一局：清空归位与计数、重建托盘与版面、重新计时。
   *  口径与省份拼图一致：换一局就彻底换，不做「继续上局」。 */
  function newGame(msg) {
    stopTimer();
    state.placed = {};
    state.placedCount = 0;
    state.miss = 0;
    state.hints = 0;
    state.elapsed = 0;
    state.done = false;
    state.selected = null;
    buildTray();
    buildSvg();
    updateHud();
    renderRecord();
    startTimer();
    say(msg || ('从托盘挑一个名字，点它在大洋或大陆上的位置——放对了给你讲它，放错了告诉你那里是谁。'));
  }

  function startTimer() {
    stopTimer();
    state.timer = setInterval(function () {
      state.elapsed += 1000;
      el.time.textContent = '用时 ' + fmtTime(state.elapsed);
    }, 1000);
  }

  function onHint() {
    if (state.done) return;
    var f = state.selected;
    if (!f) {
      var left = pieces.filter(function (x) { return !state.placed[x.id]; });
      if (!left.length) return;
      f = left[Math.floor(Math.random() * left.length)];
      selectPiece(f);
    }
    state.hints++;
    var n = nodes[f.id];
    n.classList.add('is-hint');
    setTimeout(function () { n.classList.remove('is-hint'); }, HINT_MS);
    say('「' + f.name + '」在图上闪了——它' + (f.kind === 'ocean' ? '的海域' : '') + '在' + describeZone(f) + '。提示 ' + state.hints + ' 次，结算会如实计入。');
  }

  function onResetRecord() {
    // 连点两次确认（口径同省份拼图 / 山河归位）
    if (el.resetBtn.dataset.armed === '1') {
      try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 忽略 */ }
      delete el.resetBtn.dataset.armed;
      el.resetBtn.textContent = '重置拼图成绩';
      renderRecord();
      say('成绩已清空。');
      return;
    }
    el.resetBtn.dataset.armed = '1';
    el.resetBtn.textContent = '再点一次确认清空';
    setTimeout(function () {
      delete el.resetBtn.dataset.armed;
      el.resetBtn.textContent = '重置拼图成绩';
    }, 3000);
  }

  /* ---- 启动 ---- */

  var OCEAN_BY_ID = {};

  function boot() {
    if (!MAP || !MAP.continents || !MAP.oceans) return; // 数据文件未就位（加载顺序被破坏）时静默退出
    el.map = document.querySelector('[data-st-map]');
    if (!el.map) return; // 只在大洲大洋归位页生效

    OCEAN_BY_ID = {};
    // 统一题目层：大洲带轮廓锚点（cx/cy），大洋带海面锚点（ax/ay）
    pieces = [];
    MAP.continents.forEach(function (c) {
      OCEAN_BY_ID[c.id] = null;
      pieces.push({
        id: c.id, name: c.name, tip: c.tip, kind: 'continent', ref: c,
        ax: c.cx, ay: c.cy,
      });
    });
    MAP.oceans.forEach(function (o) {
      OCEAN_BY_ID[o.id] = o;
      pieces.push({ id: o.id, name: o.name, tip: o.tip, kind: 'ocean', ref: o, ax: o.ax, ay: o.ay });
    });

    el.tray = document.querySelector('[data-st-tray]');
    el.status = document.querySelector('[data-st-status]');
    el.progress = document.querySelector('[data-st-progress]');
    el.time = document.querySelector('[data-st-time]');
    el.record = document.querySelector('[data-st-record]');
    el.newBtn = document.querySelector('[data-st-new]');
    el.hintBtn = document.querySelector('[data-st-hint]');
    el.resetBtn = document.querySelector('[data-st-reset]');
    if (!el.tray || !el.status) return;

    el.newBtn.addEventListener('click', function () { newGame(); });
    el.hintBtn.addEventListener('click', onHint);
    if (el.resetBtn) el.resetBtn.addEventListener('click', onResetRecord);

    newGame();
  }

  boot();

  /* ---- 冒烟钩子（纯函数，供确定性用例） ---- */
  global.STWorld = {
    pointInRings: pointInRings,
    lonLatAt: lonLatAt,
    oceanAt: oceanAt,
    isCaspian: isCaspian,
    continentAt: continentAt,
    bearingWord: bearingWord,
    describeZone: describeZone,
    shuffledIds: shuffledIds,
  };
})(typeof window !== 'undefined' ? window : this);
